import { X, Download, FileText, Info, GalleryThumbnails, GalleryThumbnailsIcon } from 'lucide-react';
import { useState, useEffect, useRef} from 'react';
import jsPDF from 'jspdf';
import html2pdf from 'html2pdf.js';
import { marked } from 'marked';
import LoadingText from "./LoadingText";

function getHeatmapLegendUrl(apn, property, version = 0, layer, previewRequestId = "") {
  const legendApn =
    apn === "All Parcels Combined" && previewRequestId
      ? `_combined-previews-${previewRequestId}`
      : apn || "All Parcels Combined";
  const apnPath = encodeURIComponent(legendApn);
  let propertySlug = property.toLowerCase().replace(/\s+/g, '_');
  const remap = {
    wetland_type: 'wetland_type',
    class_name: 'wetland_class',
    system_name: 'wetland_system',
    modifier: 'wetland_modifier1',
    elevation: 'elevation',
    clay: 'clay',
    sand: 'sand',
    soil : 'soil_texture_class'

  };
  
  let depthLabel = null;

console.log(propertySlug, depthLabel);
if (propertySlug.startsWith("soil_")) {
  depthLabel = propertySlug.split("^")[1]; // get the part after '_'
  propertySlug = "soil";                   // normalize prefix
}
  
  console.log(propertySlug, depthLabel);
  propertySlug = remap[propertySlug] || propertySlug;


  const cb = `${apnPath}-${encodeURIComponent(propertySlug)}-${version}`;

  const normalizedLayer = (layer || "").toLowerCase();
  console.log(normalizedLayer);

  if (normalizedLayer === "wetlands") {
    return `/api/heatmap_legends/${apnPath}/NWI/wetlands_${propertySlug}_legend.png?cb=${cb}`;
  }

  if (normalizedLayer === "elevation") {
  return `/api/heatmap_legends/${apnPath}/Elevation/${propertySlug}_legend.png?cb=${cb}`;
  }
  if (normalizedLayer === "soil") {
  return `/api/heatmap_legends/${apnPath}/Soil/depths/${depthLabel}/soil_texture_class_legend.png?cb=${cb}`;
  }
  if (normalizedLayer === "flood") {
  return `/api/heatmap_legends/${apnPath}/FEMA/flood_zones_zone_type_legend.png?cb=${cb}`;
  }
}

const WetlandLegend = ({ apn, property, heatmapReadyTick, layer, previewRequestId}) => {
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'found' | 'not_found'
  const legendUrl = getHeatmapLegendUrl(apn, property, heatmapReadyTick, layer, previewRequestId);

  // Reset whenever APN or property changes
  useEffect(() => {
    setStatus('idle');
  }, [apn, property, previewRequestId]);

  // Only attempt ONCE per (apn, property) AFTER overlays are ready
  useEffect(() => {
    if (status !== 'idle') return;

    setStatus('loading');
    const img = new Image();
    img.onload = () => setStatus('found');
    img.onerror = () => setStatus('not_found');
    img.src = legendUrl;

    // no cleanup needed; single-shot check
  }, [apn, property, heatmapReadyTick, status, legendUrl]);

  return (
    <div className="mb-6">
      <h4 className="text-md font-semibold text-gray-700 mb-2">{property.split("^")[0]} Legend</h4>

      {status === 'found' && (
        <img
          key={`${apn}-${property}`}
          src={legendUrl}
          alt={`${property} Legend`}
          className="rounded shadow max-w-full"
        />
      )}

      {status === 'loading' && (
        <div className="text-sm text-gray-500 italic">Loading legend…</div>
      )}

      {(status === 'idle' || status === 'not_found') && (
        <div className="text-sm text-gray-500 italic">No legend found.</div>
      )}
    </div>
  );
};

function depthLabelToFtString(label) {
    const cmToFt = 30.48;
    const parts = label.split('-');
    if (parts.length < 2) return "Invalid label";

    const secondNumber = parseInt(parts[1], 10);
    if (isNaN(secondNumber)) return "Invalid label";

    const ftValue = Math.round(secondNumber / cmToFt);
    return `~${ftValue}ft`;
}

const ReportPanel = ({ activeTab, setActiveTab, onClose,  reportData, reportText, reportAddress, reportAPN, handleDeleteParcel, parcelLabels, reportAnalysis, analysisSchema, combined, partialReports, liveAPNs, liveAddress, wetlandToggle, setWetlandToggle, elevationToggle, setElevationToggle, soilToggle, setSoilToggle, floodToggle, setFloodToggle, mapApiRef, reportInProgress, onDismissWidgets, onGenerateReport, bluePolygons, parcels, reportProgress, selectedApns, selectedParcelIndex, setSelectedParcelIndex, onCreditError, onCreditConsumed, previewDataLayerMapsEnabled = false}) => {
  const selectedIndex = selectedParcelIndex;
  const setSelectedIndex = setSelectedParcelIndex;
const PARTIAL_SECTION_MAP = {
  general: ["General"],
  ownership: ["Ownership", "Per Parcel Ownership", "Merged reports Ownership"],
  elevation: ["Elevation", "Per Parcel Elevation", "Merged reports Elevation"],
  flood: ["Flood", "Per Parcel Flood", "Merged reports Flood"],
  soil: ["Soil", "Per Parcel Soil", "Merged reports Soil"],
  wetlands: ["Wetlands", "Per Parcel Wetlands", "Merged reports Wetlands"],
  analysis: [
    "Merged analysis",
    "Per Parcel Evaluation/Considerations",

  ],
  fish_and_Wildlife: ["Fish_and_Wildlife", "Per Parcel Fish_and_Wildlife", "Merged reports Fish_and_Wildlife"]
};

const [previewHeatmapRequestId, setPreviewHeatmapRequestId] = useState("");

useEffect(() => {
  const onReady = (event) => {
    setPreviewHeatmapRequestId(event.detail?.requestId || "");
    setHeatmapReadyTick(t => t + 1);
  };
  window.addEventListener("heatmap:ready", onReady);
  return () => window.removeEventListener("heatmap:ready", onReady);
}, []);

function getBestPartialData(partialReports, apn, activeTab) {
  const candidates = PARTIAL_SECTION_MAP[activeTab] || [];
  for (const section of candidates) {
    const data = partialReports?.[apn]?.[section];
    if (data) return data;
  }
  return {};
}
  const hasCombined = reportAPN?.[reportAPN.length - 1] === "All Parcels Combined";
  const currentData = reportData?.[selectedIndex] || {};       
  const currentText = reportText?.[selectedIndex] || "";
  const currentAnalysis = analysisSchema?.[selectedIndex] || {};
  const [heatmapReadyTick, setHeatmapReadyTick] = useState(0);
  const [emailError, setEmailError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);
  const [selectedFloodZoneIndex, setSelectedFloodZoneIndex] = useState(0);
  const [selectedWetlandIndex, setSelectedWetlandIndex] = useState(0);


  const currentCombined = combined?.[selectedIndex] || "";
  const currentAnalysisStr = reportAnalysis?.[selectedIndex];
  const currentAddress = reportAddress?.[selectedIndex] || "";
  const apnAddress = liveAddress
  const apnList = liveAPNs; // This now always includes "all parcels" at the end
  console.log("liveAPNsliveAPNs", apnList)
  console.log("selectedApnsselectedApns", selectedApns)
  console.log("bluePolygonsbluePolygons", bluePolygons)
  console.log("selectedParcelIndexselectedParcelIndex", selectedParcelIndex)
  const currentAPN = apnList[selectedIndex] || "";
  const isCombined = currentAPN === "All Parcels Combined";
  const currentAPN_partial = apnList[selectedIndex] || "";
  const [streamMessage, setStreamMessage] = useState('');
  const apnKey = String(currentAPN_partial);
  const partialData = getBestPartialData(partialReports, apnKey, activeTab);
  const analysisPartial = getBestPartialData(partialReports, apnKey, "analysis");
  const partial_reportSchema = partialData?.report_schema;
  const generalPartial = getBestPartialData(partialReports, apnKey, "general");
  console.log("partial_reportSchema", partial_reportSchema)
  const partial_analysisSchema = partialData?.analysis_schema;
  const analysisSchemaPartial = analysisPartial?.analysis_schema || currentAnalysis || {};
  const ownershipData = partial_reportSchema || currentData?.['Ownership'] || {};
  const ownershipAnalysis = partial_analysisSchema || currentAnalysis['Ownership'] || {};
  const elevationAnalysis = partial_analysisSchema || currentAnalysis['Elevation'] || {};
  const floodAnalysis = partial_analysisSchema || currentAnalysis['Flood'] || {};
  const wetlandsAnalysis = partial_analysisSchema || currentAnalysis['Wetlands'] || {};
  const elevationData = partial_reportSchema || currentData?.['Elevation'] || {};
  const floodData = partial_reportSchema || currentData?.['Flood'] || {};
  const soilData = partial_reportSchema || currentData?.['Soil'] || {};
  const wetlandsData = partial_reportSchema || currentData?.['Wetlands'] || {};
  const fishAndWildlifeData = partial_reportSchema || currentData?.['Fish_and_Wildlife'] || {};
  const currentParcel = parcels?.[selectedIndex] || {};
  const generalData = {
    address: generalPartial?.address || currentParcel?.address || "No Information Available",
    apn: generalPartial?.apn || currentParcel?.apn || currentAPN || "No Information Available",
    acreage: generalPartial?.acreage || currentParcel?.acreage || "No Information Available",
    zoning: generalPartial?.zoning || currentParcel?.zoning || "No Information Available",
  };
  const soilPreviewSummary = soilData?.['Preview Summary'] || 'A simple soil composition summary is not available for this parcel preview.';
  const wildlifePreviewSummary = fishAndWildlifeData?.['Preview Summary'] || 'Wildlife category details are not available in this parcel preview.';
  const compactPreviewRows = (rows, keyFn, limit = 3) => {
    const seen = new Set();
    const compactRows = [];

    for (const row of rows) {
      const key = String(keyFn(row) || 'No Information Available').trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      compactRows.push(row);
      if (compactRows.length >= limit) break;
    }

    return compactRows;
  };
  const floodZoneRows = Array.isArray(floodData?.['Flood Zones'])
    ? floodData['Flood Zones'].filter(zone => zone && typeof zone === 'object')
    : [];
  const fallbackFloodZone = {
    'Flood Zone': floodData?.['Flood Zone'],
    'Zone Subtype': floodData?.['Zone Subtype'],
    'Zone Description': floodData?.['Zone Description'],
    'Base Flood Elevation': floodData?.['Base Flood Elevation'],
    'Special Flood Hazard Area': floodData?.['Special Flood Hazard Areas (SFHAs)'],
    'Risk Assessment': floodData?.['Risk Assessment'],
    'FIRM Panels': floodData?.['FIRM Panels'],
  };
  const floodPreviewSourceRows = floodZoneRows.length > 0 ? floodZoneRows : [fallbackFloodZone];
  const selectableFloodZones = compactPreviewRows(
    floodPreviewSourceRows,
    zone => `${zone?.['Flood Zone'] || 'No Information Available'}|${zone?.['Zone Subtype'] || 'No Information Available'}`
  );
  const safeSelectedFloodZoneIndex = Math.min(
    Math.max(selectedFloodZoneIndex || 0, 0),
    Math.max(selectableFloodZones.length - 1, 0)
  );
  const selectedFloodZone = selectableFloodZones[safeSelectedFloodZoneIndex] || fallbackFloodZone;
  const selectedFloodFirmPanels = (
    Array.isArray(selectedFloodZone?.['FIRM Panels']) && selectedFloodZone['FIRM Panels'].length > 0
      ? selectedFloodZone['FIRM Panels']
      : floodData?.['FIRM Panels']
  ) || [];
  const wetlandAreaRows = Array.isArray(wetlandsData?.['Wetland Areas'])
    ? wetlandsData['Wetland Areas'].filter(area => area && typeof area === 'object')
    : [];
  const fallbackWetlandArea = {
    'Wetland Type': wetlandsData?.['Wetland Type'],
    'Attribute': wetlandsData?.['Attribute'],
    'System Name': wetlandsData?.['System Name'],
    'Class Name': wetlandsData?.['Class Name'],
    'Water Regime': wetlandsData?.['Water Regime'],
    'Modifier': wetlandsData?.['Modifier'],
    'Total Acres': wetlandsData?.['Total wetland acreage within parcel'],
  };
  const wetlandTotalText = String(wetlandsData?.['Total wetland acreage within parcel'] || "").trim().toLowerCase();
  const hasWetlands =
    !!wetlandTotalText &&
    !wetlandTotalText.includes('no wetland') &&
    !wetlandTotalText.includes('no data') &&
    !wetlandTotalText.includes('not available') &&
    wetlandTotalText !== '0' &&
    wetlandTotalText !== '0.0' &&
    wetlandTotalText !== '0.00';
  const selectableWetlandAreas = hasWetlands
    ? compactPreviewRows(
        wetlandAreaRows.length > 0 ? wetlandAreaRows : [fallbackWetlandArea],
        area => area?.['Wetland Type'] || 'No Information Available'
      )
    : [];
  const safeSelectedWetlandIndex = Math.min(
    Math.max(selectedWetlandIndex || 0, 0),
    Math.max(selectableWetlandAreas.length - 1, 0)
  );
  const selectedWetlandArea = selectableWetlandAreas[safeSelectedWetlandIndex] || fallbackWetlandArea;
  useEffect(() => {
    setSelectedFloodZoneIndex(0);
  }, [currentAPN, activeTab]);
  useEffect(() => {
    setSelectedWetlandIndex(0);
  }, [currentAPN, activeTab]);
  const renderSectionStatus = (sectionData) => {
    const statusRaw = sectionData?.['Data Source Status'] || '';
    const stateRaw = sectionData?.['Data State'] || '';
    const noteRaw = sectionData?.['Section Note'] || '';
    const status = String(statusRaw).toLowerCase();
    const state = String(stateRaw).toLowerCase();
    const note = String(noteRaw).trim();

    if (!status && !state && !note) return null;

    const degraded = status === 'degraded' || state === 'api_down';
    const badgeClass = degraded
      ? 'bg-red-100 text-red-700 border-red-200'
      : 'bg-emerald-100 text-emerald-700 border-emerald-200';
    const badgeLabel = degraded ? 'Source Delayed' : 'Source Healthy';

    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Section Data Status</div>
          <span className={`text-xs px-2 py-1 rounded border ${badgeClass}`}>{badgeLabel}</span>
        </div>
        <div className="mt-2 text-sm text-gray-700">
          {note || (degraded ? 'Source temporarily unavailable.' : 'Data retrieved successfully.')}
        </div>
      </div>
    );
  };
  const tabs = ['general', 'analysis', 'wetlands', 'flood', 'elevation', 'soil', 'ownership', 'fish_and_Wildlife'];
  const [heatmapFailed, setHeatmapFailed] = useState(false);
  const visibleTabs = tabs.filter(tab => hasPartialData(partialReports, apnKey, tab));
  const [showGettingStarted, setShowGettingStarted] = useState(true);   // controls if panel is shown at all (X closes)
  const [gsCollapsed, setGsCollapsed] = useState(false);  
  const [showPopup, setShowPopup] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const analysisRequestKeyRef = useRef("");
const [soilDepthLabels, setSoilDepthLabels] = useState([]);
const [loadingDepthLabels, setLoadingDepthLabels] = useState(false);
const [showSoilDepths, setShowSoilDepths] = useState(false);

// Add this function to fetch soil depth labels
const fetchSoilDepthLabels = async () => {
  setSoilDepthLabels([]);
  setShowSoilDepths(false);

  // --- Get selected polygon ---
  let polygon = bluePolygons?.[selectedIndex];
  if (!Array.isArray(polygon) || polygon.length === 0) {
    console.warn("⚠️ No polygon selected for soil depth lookup.");
    return;
  }

  // Wrap single coordinate pair in an array
  if (typeof polygon[0] === "number") {
    polygon = [polygon];
  }

  // Filter out invalid coordinates
  const validCoords = polygon.filter(
    p => Array.isArray(p) && typeof p[0] === "number" && typeof p[1] === "number"
  );

  if (validCoords.length === 0) {
    console.warn("⚠️ Polygon has no valid coordinates.");
    return;
  }

  try {
    setLoadingDepthLabels(true);

    // Map coordinates to API requests
    const requests = validCoords.map(([lon, lat]) =>
      fetch(`/api/fetch_soil_depth_labels?${new URLSearchParams({ lat, lon }).toString()}`)
        .then(res => res.json())
        .then(data => data.depth_labels || [])
        .catch(err => {
          console.error(`❌ Error fetching soil depths for (${lat}, ${lon}):`, err);
          return [];
        })
    );

    // Wait for all responses
    const allResults = await Promise.all(requests);
    console.log("🌱 Soil depth results (all coords):", allResults);

    // Merge and deduplicate depth labels
    const mergedLabels = Array.from(new Set(allResults.flat()));
    setSoilDepthLabels(mergedLabels);
    setShowSoilDepths(true);

  } catch (err) {
    console.error("❌ Soil depth fetch failed:", err);
    setSoilDepthLabels([]);
  } finally {
    setLoadingDepthLabels(false);
  }
};


// Reset soil depths when parcel changes
useEffect(() => {
  setShowSoilDepths(false);
  setSoilDepthLabels([]);
  setSoilToggle(null);
}, [selectedIndex]);

  useEffect(() => {
  setHeatmapFailed(false);
}, [currentAPN, wetlandToggle]);

useEffect(() => {
  const onNF = (e) => {
    setHeatmapFailed(true);
  };
  window.addEventListener('heatmap:not_found', onNF);
  return () => window.removeEventListener('heatmap:not_found', onNF);
}, [selectedIndex, apnList]);

  useEffect(() => {
    if (visibleTabs.length === 0) {
      setActiveTab(null); // No tabs available, reset activeTab
    } else if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]); // Set to first available tab for this parcel
    }
    // Only run when visibleTabs or selectedIndex changes
  }, [visibleTabs, selectedIndex]);

  function hasPartialData(partialReports, apnKey, tab) {
    const candidates = PARTIAL_SECTION_MAP[tab] || [];
    if (!partialReports?.[apnKey]) return false;
    for (const section of candidates) {
      if (partialReports[apnKey][section]) return true;
    }
    return false;
  }

  const downloadReportAsPDF2 = async (markdown) => {
    try {
        const response = await fetch('/api/generate_pdf', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ markdown })
        });
        if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'landbuilder-analysis-report.pdf';
            a.click();
            window.URL.revokeObjectURL(url);
        }
    } catch (error) {
        console.error('PDF generation failed:', error);
    }
};

useEffect(() => {
  async function fetchUser() {
    try {
      const res = await fetch("/api/auth/status", {
        credentials: "include"   // important to send cookies/session
      });
      const data = await res.json();
      if (data.authenticated && data.email) {
        setAccountEmail(data.email);
        setEmailInput(data.email);
      }
      if (data.authenticated && data.user) {
        setUsernameInput(data.user);
      }
    } catch (err) {
      console.error("Failed to fetch auth status:", err);
    }
  }
  fetchUser();
}, []);


useEffect(() => {
  if (!wetlandToggle) return;
  setHeatmapReadyTick(0);             // reset at run start
}, [wetlandToggle, currentAPN]);

useEffect(() => {
  if (!elevationToggle) return;
  setHeatmapReadyTick(0);             // reset at run start
}, [elevationToggle, currentAPN]);

useEffect(() => {
  if (!soilToggle) return;
  setHeatmapReadyTick(0);             // reset at run start
}, [soilToggle, currentAPN]);

useEffect(() => {
  if (!floodToggle) return;
  setHeatmapReadyTick(0);             // reset at run start
}, [floodToggle, currentAPN]);

// //////////
 useEffect(() => {
     // On parcel change, clear the wetlands selection and any overlay/SSE.
   setWetlandToggle(null);
   setElevationToggle(null);
   setSoilToggle(null);
   setFloodToggle(null);
   setHeatmapReadyTick(0);
   mapApiRef?.current?.clearHeatmapOverlay?.();
 }, [selectedIndex]);

  useEffect(() => {
   if (activeTab !== 'wetlands') {
     setWetlandToggle(null);
     mapApiRef?.current?.clearHeatmapOverlay?.();
   }
 }, [activeTab]);

   useEffect(() => {
   if (activeTab !== 'elevation') {
     setElevationToggle(null);
     mapApiRef?.current?.clearHeatmapOverlay?.();
   }
 }, [activeTab]);

    useEffect(() => {
   if (activeTab !== 'soil') {
     setSoilToggle(null);
     mapApiRef?.current?.clearHeatmapOverlay?.();
   }
 }, [activeTab]);
// ///////////

useEffect(() => {
   if (activeTab !== 'flood') {
     setFloodToggle(null);
     mapApiRef?.current?.clearHeatmapOverlay?.();
   }
 }, [activeTab]);

  const selectedParcelCount = Array.isArray(bluePolygons)
    ? bluePolygons.length
    : Math.max(
        Array.isArray(selectedApns) ? selectedApns.length : 0,
        Array.isArray(liveAPNs) ? liveAPNs.filter(apn => apn && apn !== "All Parcels Combined").length : 0
      );
  const hasParcelSelection = selectedParcelCount > 0;
  const hasSiteData = hasParcelSelection && visibleTabs.length > 0;
  const showCenteredGettingStarted = showGettingStarted && !hasParcelSelection;
  const showTopGettingStarted = showGettingStarted && hasParcelSelection;
  const showPrePreviewActions = hasParcelSelection && !hasSiteData;

  const openAnalysisDialog = () => {
    setEmailError("");
    setSubmitError("");
    analysisRequestKeyRef.current = crypto.randomUUID();
    setShowPopup(true);
  };

  const renderGettingStarted = ({ centered = false } = {}) => (
    <div
      className={`border rounded-2xl shadow bg-white transition-all duration-500 ease-out ${
        centered ? "w-full max-w-lg" : "m-4"
      }`}
    >
      <div className="flex items-center justify-between border-b px-4 py-2 bg-gray-50 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-gray-800">Getting Started</span>
        </div>

        {!centered && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setGsCollapsed(v => !v)}
              className="text-sm text-gray-600 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-100"
              aria-expanded={!gsCollapsed}
              aria-controls="getting-started-body"
              title={gsCollapsed ? "Expand" : "Collapse"}
            >
              {gsCollapsed ? "Expand" : "Collapse"}
            </button>

            <button
              onClick={() => setShowGettingStarted(false)}
              className="text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {(centered || !gsCollapsed) && (
        <div
          id="getting-started-body"
          className={`${centered ? "px-6 py-5" : "max-h-64 overflow-y-auto px-4 py-3"} text-sm text-gray-700 space-y-3 transition-all duration-500 ease-out`}
        >
          <p className="font-medium">Welcome to Acquisition Intel!</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><em>Select</em>: Click to choose one or more parcels on the map.</li>
            <li><em>Remove</em>: Click to remove parcels from your current selection.</li>
            <li><em>Preview</em>: Click to begin the preview of your selected site.</li>
            <li>Tabs will appear under Site Data with details for your site.</li>
            {previewDataLayerMapsEnabled && <li>Use the Maps controls to view available data-layer overlays.</li>}
            <li>Use the dropdown menu to switch between parcels selected.</li>
            <li>You can additionally choose to generate a complete Due Diligence Analysis and/or create a Site Plan below.</li>
          </ul>
        </div>
      )}
    </div>
  );

  const renderNextActions = ({ centered = false } = {}) => (
    <div className={`${centered ? "w-full max-w-3xl" : "mt-12 border-t pt-4 ml-4"} transition-all duration-500 ease-out`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800">View Deeper Insights & Unlock your Site's Potential</h3>
        {!centered && (
          <button
            onClick={onDismissWidgets}
            className="text-gray-400 hover:text-gray-600"
            title="Hide"
          >
            ×
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 p-4 shadow-sm flex flex-col bg-white">
          <div className="text-sm text-gray-700 mb-3 flex-grow">
            <h3 className="text-sm font-semibold text-gray-800">Due Diligence Analysis</h3>
            <p className="text-gray-700 mb-4">Make smarter land decisions—fast.</p>
            <ul className="list-disc pl-5 space-y-2 text-gray-700">
              <li><strong>Comprehensive analysis:</strong> Our AI scans and aggregates ownership, flood risk, soil conditions, wetlands, and more from trusted data sources.</li>
              <li><strong>Significant time savings:</strong> Replace weeks of manual research with results delivered in minutes.</li>
              <li><strong>Cost-effective insights:</strong> Avoid paying thousands of dollars to third-party consultants.</li>
              <li><strong>Professional output:</strong> Receive a polished, complete PDF analysis you can rely on by email.</li>
            </ul>
          </div>
          <div className="mt-4 flex justify-center">
            <button
              onClick={openAnalysisDialog}
              className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
            >
              Generate Analysis
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 p-4 shadow-sm flex flex-col bg-white">
          <div className="text-sm text-gray-700 mb-3 flex-grow">
            <h3 className="text-sm font-semibold text-gray-800">Site Plan Creation</h3>
            <p className="text-gray-700 mb-4">Visualize your Site's potential instantly.</p>
            <ul className="list-disc pl-5 space-y-2 text-gray-700">
              <li><strong>Build-ready clarity:</strong> Our AI generates a clear site plan that aligns your team and reduces back-and-forth.</li>
              <li><strong>Stakeholder-friendly:</strong> Share with investors, contractors, or city officials to accelerate reviews and approvals.</li>
              <li><strong>Save time and money:</strong> Cut iterations, avoid guesswork, and move forward with confidence.</li>
              <li><strong>Flexible deliverables:</strong> Receive your plan as a PDF or CAD file by email so it fits directly into your workflow.</li>
            </ul>
          </div>
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => setShowComingSoonPopup(true)}
              className="px-3 py-2 rounded-md bg-green-600 text-white text-sm hover:bg-green-700 transition-colors"
            >
              Create Site Plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (

<div className="w-full lg:w-1/2 bg-white shadow-md overflow-y-auto relative">

{showCenteredGettingStarted && (
  <div className="min-h-full flex items-center justify-center p-8 transition-all duration-500 ease-out">
    {renderGettingStarted({ centered: true })}
  </div>
)}

{showTopGettingStarted && (
  <div className="transition-all duration-500 ease-out">
    {renderGettingStarted()}
  </div>
)}

{showPrePreviewActions && (
  <div className="min-h-[calc(100vh-220px)] flex items-center justify-center p-6 transition-all duration-500 ease-out">
    {renderNextActions({ centered: true })}
  </div>
)}

{hasSiteData && (
  <>
  <div className="p-4 border-b flex justify-between items-center">
    <div>  
    <div className="flex items-center space-x-2">
  <h2 className="text-xl font-semibold">Site Data</h2>

  {/* Spinner with tooltip on hover */}
  <div className="relative group flex items-center space-x-5 overflow-visible">
  {/* Spinner */}
  <div
    id="spinner"
    className={`${reportProgress ? '' : 'hidden'} animate-spin rounded-full h-5 w-5 min-w-[1.25rem] min-h-[1.25rem] aspect-square border-2 border-blue-500 border-t-transparent`}
  ></div>

  {/* Tooltip */}
  <div className="absolute left-7 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
    (analysis generation can take ~ 5 mins)
  </div>

  {/* Streaming messages container */}
  <div id="loader" className={`${reportProgress ? '' : 'hidden'} max-w-xs text-xs font-mono loading-text`}>
  {streamMessage || 'Starting preview...'}
</div>
</div>
{/* Heatmap Loader & Spinner */}
{previewDataLayerMapsEnabled && <div className="relative group flex items-center space-x-5 overflow-visible ml-4">
  {/* Heatmap spinner */}
  <div
    id="heatmap-spinner"
    className="hidden animate-spin rounded-full h-5 w-5 min-w-[1.25rem] min-h-[1.25rem]
               aspect-square border-2 border-green-500 border-t-transparent"
  ></div>

  {/* Tooltip for heatmap */}
  <div className="absolute left-7 top-1/2 -translate-y-1/2 bg-gray-700 text-white text-xs rounded px-2 py-1
                  opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
    (map generation is usually quick)
  </div>

  {/* Heatmap streaming text */}
  <div id="heatmap-loader" className="hidden max-w-xs text-xs font-mono loading-text">
    Loading map...
  </div>
</div>}


</div>

    </div>

<div className="flex flex-col space-y-1">
        </div>
      </div>

      <div className="flex border-b items-center">
  {/* Tabs scroll if too many */}
  <div className="flex overflow-x-auto flex-1">
    {visibleTabs.map(tab => (
      <button
        key={tab}
        className={`px-4 py-2 text-sm font-medium ${
          activeTab === tab
            ? 'border-b-2 border-blue-500 text-blue-600'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
        }`}
        onClick={() => setActiveTab(tab)}
      >
        {tab.charAt(0).toUpperCase() + tab.slice(1)}
      </button>
    ))}
  </div>

  {/* Pinned dropdown on right side */}
  {reportInProgress && (
    <div className="flex items-center space-x-2 pl-2">
      <select
        value={selectedIndex}
        onChange={(e) => setSelectedIndex(Number(e.target.value))}
        className="border border-gray-300 text-sm rounded-md px-2 py-1 text-gray-700 hover:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        {apnList.map((apn, idx) => (
          <option key={apn || idx} value={idx}>
            {`Parcel ${apn}`}
          </option>
        ))}
      </select>
    </div>
  )}
</div>

            {visibleTabs.length === 0 || !activeTab ? (
              <div className="p-6 flex flex-col items-center justify-center min-h-[200px] text-gray-400">
              </div>
            ) : (
            <div className="p-6">
            
              {activeTab === 'general' && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="text-blue-500" size={24} />
                    <h3 className="text-lg font-semibold">Site Information</h3>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="text-sm text-gray-600 mb-4">
                      <p>
                        <span className="font-medium">Address: </span>
                          {generalData.address}
                      </p>

                      <p>
                        <span className="font-medium">APN: </span>
                        {generalData.apn}
                      </p>
                      <p>
                        <span className="font-medium">Total Acreage: </span>
                        {generalData.acreage}
                      </p>
                      <p>
                        <span className="font-medium">Zoning: </span>
                        {generalData.zoning}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 italic mt-2">
                    More information about parcel location, zoning, land use, and due diligence context can be found in the due diligence analysis.
                  </p>
                </div>

                
              )}

              {activeTab === 'ownership' && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="text-blue-500" size={24} />
                    <h3 className="text-lg font-semibold">Ownership Information</h3>
                  </div>
                  {renderSectionStatus(ownershipData)}
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="font-medium text-gray-700 mb-2">Current Ownership</div>
                    <div className="text-sm text-gray-600 mb-4">
                      <p> <span className="font-medium"> Owner(s): </span> {ownershipData?.['Owner(s)'] || "No Information Available"}</p>
                      <p> <span className="font-medium">Ownership Type: </span>{ownershipData?.['Ownership Type'] || "No Information Available"}</p>
                      <p><span className="font-medium">Absentee Owner Status: </span>{ownershipData?.['Absentee Owner Status'] || "No Information Available"}</p>
                      <p><span className="font-medium">Corporate Indicator: </span>{ownershipData?.['Corporate Indicator'] || "No Information Available"}</p>
                    </div>
                    
                    <div className="font-medium text-gray-700 mb-2">Sale History</div>
                    <div className="text-sm text-gray-600 mb-4">

                      <p> <span className="font-medium"> Buyer Name: </span> {ownershipData?.['Sale History']?.['Buyer Name'] || "No Information Available"}</p>
                      <p> <span className="font-medium">Seller: </span>{ownershipData?.['Sale History']?.['Seller'] || "No Information Available"}</p>
                      <p><span className="font-medium">Sale Search Date: </span>{ownershipData?.['Sale History']?.['Sale Search Date'] || "No Information Available"}</p>
                      <p> <span className="font-medium"> Transaction Date: </span> {ownershipData?.['Sale History']?.['Transaction Date'] || "No Information Available"}</p>
                      <p> <span className="font-medium">Sale Amount: </span>{ownershipData?.['Sale History']?.['Sale Amount'] || "No Information Available"}</p>
                      <p><span className="font-medium"> Transaction Type: </span>{ownershipData?.['Sale History']?.['Transaction Type'] || "No Information Available"}</p>
                    </div>
                    <div className="font-medium text-gray-700 mb-2">Foreclosures</div>
                    <div className="text-sm text-gray-600 mb-4">             
                      <p> <span className="font-medium"> Recording Date: </span> {ownershipData?.['Foreclosures']?.['Recording Date'] || "No Information Available"}</p>
                      <p> <span className="font-medium">Trustor Last Name: </span>{ownershipData?.['Foreclosures']?.['Trustor Last Name'] || "No Information Available"}</p>
                      <p><span className="font-medium">Trustor First Name: </span>{ownershipData?.['Foreclosures']?.['Trustor First Name'] || "No Information Available"}</p>
                      <p> <span className="font-medium"> Trustee First Name: </span> {ownershipData?.['Foreclosures']?.['Trustee First Name'] || "No Information Available"}</p>
                      <p> <span className="font-medium">Original Loan Amount: </span>{ownershipData?.['Foreclosures']?.['Original Loan Amount'] || "No Information Available"}</p>
                      <p><span className="font-medium"> Original Loan Date: </span>{ownershipData?.['Foreclosures']?.['Original Loan Date'] || "No Information Available"}</p>   
                      <p><span className="font-medium"> Distress Type: </span>{ownershipData?.['Foreclosures']?.['Distress Type'] || "No Information Available"}</p>         
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 italic mt-2">
                    Source: Ownership data derived from county assessor and recorder databases, along with trusted third-party aggregators.
                  </p>
                  <p className="text-xs text-gray-500 italic mt-2">
                    More information about ownership records, sale history, and title-related context can be found in the due diligence analysis.
                  </p>
                                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="prose text-sm text-gray-700">
                              {ownershipAnalysis?.Pros && (
                                <>
                                  <div className="font-medium">Pros</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(ownershipAnalysis.Pros || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                              {ownershipAnalysis?.Cons && (
                                <>
                                  <div className="font-medium">Cons</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(ownershipAnalysis.Cons || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                </div>

                
              )}
              
              {activeTab === 'elevation' && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="text-blue-500" size={24} />
                    <h3 className="text-lg font-semibold">Elevation Analysis</h3>
                  </div>
                  {renderSectionStatus(elevationData)}
                  {previewDataLayerMapsEnabled && <>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Maps</h3>
                  <div className="mb-4 flex space-x-2">

                    {['Elevation'].map(option => (
                      <button
                        key={option}
                        onClick={() => {
                          window.setIsGeneratingHeatmaps(true);
                          setElevationToggle(null);
                          requestAnimationFrame(() => setElevationToggle(option));
                          // setElevationToggle(option)
                        }}
                        // onClick={() => setWetlandToggle(option)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${elevationToggle === option ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>

                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                        {elevationToggle && (
                            heatmapFailed ? (
                              <div className="text-sm text-gray-500 italic">No legend found.</div>
                              //   <div className="text-sm text-red-500 font-medium">
                              //   ⚠️ No heatmap available for this parcel/layer.
                              // </div>
                            ) : (
                              heatmapReadyTick > 0 ? (
                                <WetlandLegend
                                  apn={currentAPN}
                                  property={elevationToggle}
                                  heatmapReadyTick={heatmapReadyTick}
                                  previewRequestId={previewHeatmapRequestId}
                                  layer = {activeTab}
                                />
                              ) : (
                                <div className="text-sm text-gray-500 italic">Generating map…</div>
                              )
                            )
                          )}
                        </div>
                  </>}

                  {/* ELEVATION SECTION */}
                  <div className="mb-4 text-gray-700 font-medium">Elevation Statistics</div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500 mb-2">Minimum Elevation</div>
                      <div className="text-xl font-bold">{elevationData?.['Minimum Elevation'] || "No Information Available"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500 mb-2">Maximum Elevation</div>
                      <div className="text-xl font-bold">{elevationData?.['Maximum Elevation'] || "No Information Available"}</div>
                    </div>
                  </div>

                  {/* SLOPE SECTION */}
                  <div className="mb-4 text-gray-700 font-medium">Slope Statistics</div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500 mb-2">Minimum Slope</div>
                      <div className="text-xl font-bold">{elevationData?.['Minimum Slope'] || "No Information Available"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500 mb-2">Maximum Slope</div>
                      <div className="text-xl font-bold">{elevationData?.['Maximum Slope'] || "No Information Available"}</div>
                    </div>
                  </div>

                  {/* CONVERGENCE INDEX SECTION */}
                  {/* <div className="mb-4 text-gray-700 font-medium">Convergence Index Statistics</div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500 mb-2">Minimum Convergence Index</div>
                      <div className="text-xl font-bold">{elevationData?.['Minimum Convergence Index'] || "No Information Available"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500 mb-2">Maximum Convergence Index</div>
                      <div className="text-xl font-bold">{elevationData?.['Maximum Convergence Index'] || "No Information Available"}</div>
                    </div>
                  </div> */}

                  {/* LANDFORM SECTION */}
                  {/* <div className="mb-4 text-gray-700 font-medium">Landform Classification Statistics</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500 mb-2">Minimum Landform Classification</div>
                      <div className="text-xl font-bold">{elevationData?.['Minimum Landform Classification'] || "No Information Available"}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium text-gray-500 mb-2">Maximum Landform Classification</div>
                      <div className="text-xl font-bold">{elevationData?.['Maximum Landform Classification'] || "No Information Available"}</div>
                    </div>
                  </div> */}
                  <p className="text-xs text-gray-500 italic mt-2">
                    Source: Elevation data derived from USGS digital elevation models.
                  </p>
                  <p className="text-xs text-gray-500 italic mt-2">
                    More information about elevation, slope, and topographic constraints can be found in the due diligence analysis.
                  </p>

                                            {/* Elevation */}
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="prose text-sm text-gray-700">
                              {elevationAnalysis?.Pros && (
                                <>
                                  <div className="font-medium">Pros</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(elevationAnalysis.Pros || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                              {elevationAnalysis?.Cons && (
                                <>
                                  <div className="font-medium">Cons</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(elevationAnalysis.Cons || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                </div>
              )}
                            
              {activeTab === 'flood' && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="text-blue-500" size={24} />
                    <h3 className="text-lg font-semibold">Flood Risk Assessment</h3>
                  </div>
                  {renderSectionStatus(floodData)}
                  <div className="bg-green-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mr-4 flex-shrink-0">
                        {!isCombined && (
                          <span className="text-green-600 font-bold text-xl">
                            {selectedFloodZone?.['Flood Zone'] || ""}
                          </span>
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-green-800">Zone {selectedFloodZone?.['Flood Zone'] || "No Information Available"}</h4>
                        <p className="text-sm text-green-700">{selectedFloodZone?.['Zone Subtype'] || "No Information Available"}</p>
                      </div>
                    </div>
                    {selectableFloodZones.length > 0 && (
                      <div className="mt-4 overflow-x-auto">
                        <div className="font-medium text-green-800 mb-2">Flood Zones</div>
                        <table className="min-w-full text-sm text-left text-green-800">
                          <thead>
                            <tr className="border-b border-green-200">
                              <th className="py-2 pr-4 font-medium">Zone</th>
                              <th className="py-2 pr-4 font-medium">Zone Subtype</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectableFloodZones.map((zone, idx) => {
                              const isSelected = idx === safeSelectedFloodZoneIndex;
                              return (
                                <tr
                                  key={`${zone['Flood Zone'] || 'zone'}-${zone['Zone Subtype'] || 'subtype'}-${idx}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setSelectedFloodZoneIndex(idx)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      setSelectedFloodZoneIndex(idx);
                                    }
                                  }}
                                  className={`cursor-pointer border-b border-green-100 last:border-b-0 transition-colors ${isSelected ? 'bg-green-100 text-green-900' : 'hover:bg-green-100/60'}`}
                                >
                                  <td className="py-2 pr-4 font-medium">{zone['Flood Zone'] || "No Information Available"}</td>
                                  <td className="py-2 pr-4">{zone['Zone Subtype'] || "No Information Available"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {floodPreviewSourceRows.length > selectableFloodZones.length && (
                          <p className="mt-2 text-xs text-green-700">
                            Showing up to 3 unique flood zone/subtype combinations. Generate analysis for complete details.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {previewDataLayerMapsEnabled && <>
                  {/* Flood Maps addition */}
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Maps</h3>
                  <div className="mb-4 flex space-x-2">

                    {['Flood Zone Type'].map(option => (
                      <button
                        key={option}
                        onClick={() => {
                          window.setIsGeneratingHeatmaps(true);
                          setFloodToggle(null);
                          requestAnimationFrame(() => setFloodToggle(option));
                          // setElevationToggle(option)
                        }}
                        // onClick={() => setWetlandToggle(option)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${floodToggle === option ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  </>}
                
                
                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
             

                        {floodToggle && (
                            console.log("heatmap failed:", heatmapFailed),
                            heatmapFailed ? (
                              <div className="text-sm text-gray-500 italic">No legend found.</div>
                              //   <div className="text-sm text-red-500 font-medium">
                              //   ⚠️ No heatmap available for this parcel/layer.
                              // </div>
                            ) : (
                              heatmapReadyTick > 0 ? (
                                <WetlandLegend
                                  apn={currentAPN}
                                  property={floodToggle}
                                  heatmapReadyTick={heatmapReadyTick}
                                  previewRequestId={previewHeatmapRequestId}
                                  layer = {activeTab}
                                />
                              ) : (
                                <div className="text-sm text-gray-500 italic">Generating map…</div>
                              )
                            )
                          )}
                  </div>

                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="font-medium text-gray-700 mb-2">FEMA Classification</div>
                    <div className="text-sm text-gray-600 mb-4">
                      <p><span className="font-medium">FEMA Flood Zone:</span> {selectedFloodZone?.['Flood Zone'] || "No Information Available"}</p>
                      <p><span className="font-medium">Zone Subtype:</span> {selectedFloodZone?.['Zone Subtype'] || "No Information Available"}</p>
                      <p><span className="font-medium">FEMA Flood Zone Designation:</span> {selectedFloodZone?.['Zone Description'] || "No Information Available"}</p>
                      <p><span className="font-medium">Special Flood Hazard Areas (SFHAs):</span> {selectedFloodZone?.['Special Flood Hazard Area'] || "No Information Available"}</p>
                      <p><span className="font-medium">Base Flood Elevation:</span> {selectedFloodZone?.['Base Flood Elevation'] || "No Information Available"}</p>
                      <p><span className="font-medium">Risk Assessment:</span> {selectedFloodZone?.['Risk Assessment'] || floodData?.['Risk Assessment'] || "No Information Available"}</p>
                    </div>
                    
                    <div className="font-medium text-gray-700 mb-2">FIRM Panel Information</div>
                    <div className="text-sm text-gray-600">
                      {selectedFloodFirmPanels?.length > 0 ? (
                        selectedFloodFirmPanels.map((panel, idx) => (
                          <p key={idx}>
                            <span className="font-medium">FIRM Panel {idx + 1}:</span> {panel['Panel ID']}, Panel Number: {panel['Panel Number']}, Effective Date: {panel['Effective Date']}
                          </p>
                        ))
                      ) : (
                        <p>FIRM Panel No Information Available</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 italic mt-2">
                    Source: Flood data derived from FEMA’s Flood Insurance Rate Maps (FIRMs) and NFHL layers.
                  </p>
                  <p className="text-xs text-gray-500 italic mt-2">
                    More information about FEMA flood zones, FIRM panels, and floodplain implications can be found in the due diligence analysis.
                  </p>
                                            {/* Flood Risk */}
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="prose text-sm text-gray-700">
                              {floodAnalysis?.Pros && (
                                <>
                                  <div className="font-medium">Pros</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(floodAnalysis.Pros || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                              {floodAnalysis?.Cons && (
                                <>
                                  <div className="font-medium">Cons</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(floodAnalysis.Cons || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                </div>
              )}
              
              {/*{activeTab === 'soil' && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="text-blue-500" size={24} />
                    <h3 className="text-lg font-semibold">Soil Risk Assessment</h3>
                  </div>

                  {/* <h3 className="text-lg font-semibold text-gray-800 mb-4">Heatmaps</h3> *\/}
                  {/* <div className="mb-4 flex space-x-2">

                    {['Clay', 'Sand'].map(option => (
                      <button
                        key={option}
                        onClick={() => {
                          window.setIsGeneratingHeatmaps(true);
                          // setSoilToggle(option)
                          setSoilToggle(null);
                          requestAnimationFrame(() => setSoilToggle(option));
                        }}
                        // onClick={() => setWetlandToggle(option)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${soilToggle === option ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div> *\/}

                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                        {soilToggle && (
                            heatmapFailed ? (
                              <div className="text-sm text-gray-500 italic">No legend found.</div>
                              // <div className="text-sm text-red-500 font-medium">
                              //   ⚠️ No heatmap available for this parcel/layer.
                              // </div>
                            ) : (
                              heatmapReadyTick > 0 ? (
                                <WetlandLegend
                                  apn={currentAPN}
                                  property={soilToggle}
                                  heatmapReadyTick={heatmapReadyTick}
                                  previewRequestId={previewHeatmapRequestId}
                                  layer = {activeTab}
                                />
                              ) : (
                                <div className="text-sm text-gray-500 italic">Generating map…</div>
                              )
                            )
                          )}
                        </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="font-medium text-gray-700 mb-2">Soil Characteristics</div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                      <div>
                        <p className="font-medium">Soil Texture Class:</p>
                        <p>{soilData?.['Soil Texture Class'] || "No Information Available"}</p>
                        {(() => {
                          const otherClasses = Array.isArray(soilData?.['Soil Texture Classes'])
                            ? soilData['Soil Texture Classes'].filter(cls => cls && cls !== soilData?.['Soil Texture Class'])
                            : [];
                          return otherClasses.length > 0 ? (
                            <p className="text-xs text-gray-500 mt-1">
                              Other sampled classes: {otherClasses.join(', ')}
                            </p>
                          ) : null;
                        })()}
                      </div>
                      <div>
                        <p className="font-medium">Permeability:</p>
                        <p>{soilData?.['Permeability'] || "No Information Available"}</p>
                      </div>
                      <div>
                        <p className="font-medium">Clay Content:</p>
                        <p>{soilData?.['Clay Content'] || "No Information Available"}</p>
                      </div>
                                            <div>
                        <p className="font-medium">Silt Content:</p>
                        <p>{soilData?.['Silt Content'] || "No Information Available"}</p>
                      </div>
                                            <div>
                        <p className="font-medium">Sand Content:</p>
                        <p>{soilData?.['Sand Content'] || "No Information Available"}</p>
                      </div>
                      <div>
                        <p className="font-medium">Bulk Density:</p>
                        <p>{soilData?.['Bulk Density'] || "No Information Available"}</p>
                      </div>
                    </div>
                    
                    <div className="font-medium text-gray-700 mb-2">Agricultural Suitability</div>
                    <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <p className="font-medium">Soil Compaction:</p>
                        <p>{soilData?.['Soil Compaction'] || "No Information Available"}</p>
                      </div>
                                            <div>
                        <p className="font-medium">Soil PH:</p>
                        <p>{soilData?.['Soil PH'] || "No Information Available"}</p>
                      </div>
                      <div>
                        <p className="font-medium">Soil Organic Carbon:</p>
                        <p>{soilData?.['Soil Organic Carbon'] || "No Information Available"}</p>
                      </div>
                                           {/* <div>
                        <p className="font-medium">Available Water Capacity:</p>
                        <p>{soilData?.['Available Water Capacity'] || "No Information Available"}</p>
                      </div> *\/}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 italic mt-2">
                    Source: Soil data derived from USDA Web Soil Survey.
                  </p>

                                                                {/* Soil *\/}
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="prose text-sm text-gray-700">
                              {soilAnalysis?.Pros && (
                                <>
                                  <div className="font-medium">Pros</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(soilAnalysis.Pros || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                              {soilAnalysis?.Cons && (
                                <>
                                  <div className="font-medium">Cons</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(soilAnalysis.Cons || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                </div>
              )}
              */}

              {activeTab === 'soil' && (
  <div>
    <div className="flex items-center space-x-2 mb-4">
      <FileText className="text-blue-500" size={24} />
      <h3 className="text-lg font-semibold">Soil Risk Assessment</h3>
    </div>
    {renderSectionStatus(soilData)}

    <div className="bg-green-50 rounded-lg p-4 mb-4">
      <div className="font-medium text-green-800 mb-2">Soil Classification</div>
      <p className="text-sm text-green-900 leading-6">
        {soilPreviewSummary}
      </p>
    </div>

    <p className="text-xs text-gray-500 italic mt-2">
      Source: Soil data derived from USDA Web Soil Survey.
    </p>
    <p className="text-xs text-gray-500 italic mt-2">
      More information about soil percentages of clay, sand, silt, compaction, permeability, estimated soil classifications, and depth-specific conditions can be found in the due diligence analysis.
    </p>
  </div>
)}
              
              {activeTab === 'wetlands' && (
                
                <div>

                  <div className="flex items-center space-x-2 mb-4">
                    <FileText className="text-blue-500" size={24} />
                    <h3 className="text-lg font-semibold">Wetlands Assessment</h3>
                  </div>
                  {renderSectionStatus(wetlandsData)}

                    <div className="bg-blue-50 p-4 rounded-lg mb-6">
                    <div className="font-bold text-blue-800">{wetlandsData?.['Total wetland acreage within parcel'] || "No Information Available"}</div>
                    <div className="text-sm text-blue-600">Total wetland acreage within parcel</div>
                  </div>
                  {hasWetlands && (
                    <>
                      {selectableWetlandAreas.length > 0 && (
                        <div className="bg-blue-50 rounded-lg p-4 mb-6">
                          <div className="font-medium text-blue-800 mb-3">Wetland Areas</div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-left text-blue-800">
                              <thead>
                                <tr className="border-b border-blue-200">
                                  <th className="py-2 pr-4 font-medium">Wetland Type</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectableWetlandAreas.map((area, idx) => {
                                  const isSelected = idx === safeSelectedWetlandIndex;
                                  return (
                                    <tr
                                      key={`${area['Wetland Type'] || 'wetland'}-${area['Class Name'] || 'class'}-${idx}`}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => setSelectedWetlandIndex(idx)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          setSelectedWetlandIndex(idx);
                                        }
                                      }}
                                      className={`cursor-pointer border-b border-blue-100 last:border-b-0 transition-colors ${isSelected ? 'bg-blue-100 text-blue-900' : 'hover:bg-blue-100/60'}`}
                                    >
                                      <td className="py-2 pr-4 font-medium">{area['Wetland Type'] || "No Information Available"}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            {(wetlandAreaRows.length > selectableWetlandAreas.length) && (
                              <p className="mt-2 text-xs text-blue-700">
                                Showing up to 3 unique wetland types. Generate analysis for complete wetland details.
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {previewDataLayerMapsEnabled && <>
                      <div className="mt-8">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Maps</h3>
                        <div className="mb-4 flex space-x-2">
                          {['Wetland Type', 'Class Name', 'System Name', 'Modifier'].map(option => (
                            <button
                              key={option}
                              onClick={() => {
                                window.setIsGeneratingHeatmaps(true);
                                setWetlandToggle(null);
                                requestAnimationFrame(() => setWetlandToggle(option));
                              }}
                              className={`px-3 py-1 rounded-md text-sm font-medium ${wetlandToggle === option ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        {wetlandToggle && (
                          heatmapFailed ? (
                            <div className="text-sm text-gray-500 italic">No legend found.</div>
                          ) : (
                            heatmapReadyTick > 0 ? (
                              <WetlandLegend
                                apn={currentAPN}
                                property={wetlandToggle}
                                heatmapReadyTick={heatmapReadyTick}
                                previewRequestId={previewHeatmapRequestId}
                                layer = {activeTab}
                              />
                            ) : (
                              <div className="text-sm text-gray-500 italic">Generating map…</div>
                            )
                          )
                        )}
                      </div>
                      </>}

                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <div className="font-medium text-gray-700 mb-2">Wetland Classification</div>
                        <div className="text-sm text-gray-600 mb-4 space-y-3">
                          <div>
                            <p className="font-medium">Attribute:</p>
                            <p className="ml-4">• {selectedWetlandArea?.['Attribute'] || "No Information Available"}</p>
                          </div>
                          <div>
                            <p className="font-medium">Wetland Type:</p>
                            <p className="ml-4">• {selectedWetlandArea?.['Wetland Type'] || "No Information Available"}</p>
                          </div>
                          <div>
                            <p className="font-medium">System Name:</p>
                            <p className="ml-4">• {selectedWetlandArea?.['System Name'] || "No Information Available"}</p>
                          </div>
                          <div>
                            <p className="font-medium">Class Name:</p>
                            <p className="ml-4">• {selectedWetlandArea?.['Class Name'] || "No Information Available"}</p>
                          </div>
                          <div>
                            <p className="font-medium">Modifier:</p>
                            <p className="ml-4">• {selectedWetlandArea?.['Modifier'] || "No Information Available"}</p>
                          </div>
                        </div>
                        
                        <div className="font-medium text-gray-700 mb-2">Water Regime</div>
                        <p className="text-sm text-gray-600">
                          {selectedWetlandArea?.['Water Regime'] || "No Information Available"}
                        </p>
                      </div>
                    </>
                  )}
                  <p className="text-xs text-gray-500 italic mt-2">
                    Source: Wetland data derived from U.S. Fish and Wildlife Service’s National Wetlands Inventory.
                  </p>
                  <p className="text-xs text-gray-500 italic mt-2">
                    More information about wetland classifications, acreage, water regime, and regulatory implications can be found in the due diligence analysis.
                  </p>
                                            {/* Wetlands */}
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="prose text-sm text-gray-700">
                              {wetlandsAnalysis?.Pros && (
                                <>
                                  <div className="font-medium">Pros</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(wetlandsAnalysis.Pros || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                              {wetlandsAnalysis?.Cons && (
                                <>
                                  <div className="font-medium">Cons</div>
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: marked(wetlandsAnalysis.Cons || "No Information Available")
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                </div>
              )}

              {activeTab === 'fish_and_Wildlife' && (
  <div>
    <div className="flex items-center space-x-2 mb-4">
      <FileText className="text-green-600" size={24} />
      <h3 className="text-lg font-semibold">Fish and Wildlife Habitat Assessment</h3>
    </div>
    {renderSectionStatus(fishAndWildlifeData)}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="font-bold text-green-800 text-lg">
          {fishAndWildlifeData?.["Total critical habitat acreage within parcel"] || "No Habitats Found"}
        </div>
        <div className="text-sm text-green-600">
          Total critical habitat acreage within parcel
        </div>
      </div>
      <div className="bg-emerald-50 p-4 rounded-lg">
        <div className="font-bold text-emerald-800 text-lg">
          {fishAndWildlifeData?.["Average critical habitat proximity from parcel (miles)"] ?? "No Habitats Found"}
        </div>
        <div className="text-sm text-emerald-600">
          Average critical habitat proximity from parcel (miles)
        </div>
      </div>
    </div>

    <div className="bg-white shadow rounded-lg border border-gray-200 p-4 mb-4">
      <div className="font-medium text-gray-800 mb-2">Wildlife Category Summary</div>
      <p className="text-sm text-gray-600 leading-6">
        {wildlifePreviewSummary}
      </p>
    </div>

    <p className="text-xs text-gray-500 italic mt-4">
      Source: U.S. Fish & Wildlife Service – IPaC Environmental Review
    </p>
    <p className="text-xs text-gray-500 italic mt-2">
      More information about affected species, critical habitat, proximity, and regulatory context can be found in the due diligence analysis.
    </p>
  </div>
)}

            </div>
          )}
  </>
)}

          {/* Inline “Next Actions” widgets at the bottom of the report */}
{hasSiteData && reportInProgress && renderNextActions()}

{showPopup && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[2000]">
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full text-center">
      <h2 className="text-lg font-semibold text-gray-800 mb-3">
        Analysis will be sent to your account email
      </h2>

      <input
        type="email"
        value={emailInput}
        readOnly
        disabled
        placeholder="Account email unavailable"
        className="w-full border border-gray-200 rounded-md px-3 py-2 mb-2 text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
      />

      <p className="text-xs text-gray-500 mb-4">
        This email comes from the signed-in account and cannot be changed here.
      </p>

      {emailError && (
        <p className="text-sm text-red-500 mb-2">{emailError}</p>
      )}

      {submitError && (
        <p className="text-sm text-red-500 mb-2">{submitError}</p>
      )}

      {/* Buttons */}
      <div className="flex space-x-2 justify-center">
        <button
          onClick={async () => {
            const submissionEmail = (accountEmail || emailInput || "").trim();
            const valid = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(submissionEmail);
            if (!valid) {
              setEmailError("Your account does not have a valid email associated with it.");
              return; // keep popup open
            }         
            setSubmitError("");

            try {
              await fetch("/api/track_interaction", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  username: usernameInput,
                  event_name: "generate_report_submit",
                  extra: JSON.stringify({ apn: apnList}),
                }),
              });

              fetch("/api/generate_report_full", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  coords: bluePolygons,
                  email: submissionEmail,
                  idempotency_key: analysisRequestKeyRef.current ||
                    (analysisRequestKeyRef.current = crypto.randomUUID()),
                }),
              })
                .then(async (response) => {
                  let payload = null;
                  try {
                    payload = await response.json();
                  } catch (parseError) {
                    payload = null;
                  }
                  if (!response.ok) {
                    if (response.status === 402 && payload?.error === "insufficient_credits") {
                      setShowSuccessPopup(false);
                      onCreditConsumed?.(payload.credit_type, payload.remaining_credits);
                      onCreditError?.(payload);
                      return;
                    }
                    throw new Error(payload?.message || "We could not accept your analysis request right now.");
                  }

                  if (!payload?.idempotent) {
                    onCreditConsumed?.("analysis");
                  }
                })
                .catch((e) => {
                  console.error(e);
                });

              // close popup after submit
              setShowPopup(false);
              setShowSuccessPopup(true);
              // optional: show success feedback
            } catch (e) {
              console.error(e);
              setSubmitError(e?.message || "We could not accept your analysis request right now.");
            }
          }}
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
        >
          Submit
        </button>

        <button
          onClick={() => {
            setEmailError("");
            setSubmitError("");
            setShowPopup(false);
          }}
          className="px-4 py-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}


{showSuccessPopup && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[2000]">
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full text-center">
      <h2 className="text-lg font-semibold text-gray-800 mb-3">
        Thanks for requesting an analysis!
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        You should receive your analysis at <strong>{accountEmail || emailInput}</strong> within 24 hours.
      </p>
      <button
        onClick={() => setShowSuccessPopup(false)}
        className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
      >
        Close
      </button>
    </div>
  </div>
)}

{showComingSoonPopup && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[2000]">
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full text-center">
      <h2 className="text-lg font-semibold text-gray-800 mb-3">
        Coming Soon!
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Site plan generation will be available soon. Stay tuned!
      </p>
      <button
        onClick={() => setShowComingSoonPopup(false)}
        className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
      >
        Close
      </button>
    </div>
  </div>
)}


    </div>
  );
};

export default ReportPanel;
